import React from 'react';
import {
  AppstoreOutlined,
  CalendarOutlined,
  CheckOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  FileTextOutlined,
  LinkOutlined,
  MessageOutlined,
  RobotOutlined,
  TagsOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { DatePicker, Tooltip } from 'antd';
import type { Task, TaskLink, TaskPriority } from '../../api/tasks/interface';
import { type TagCount, dueBucket, formatDue, nextPriority } from './grouping';
import styles from './TasksTab.module.scss';

const PRIORITY_LABEL: Record<TaskPriority, string> = {
  high: 'High',
  med: 'Med',
  low: 'Low',
};

const PRIORITY_CLASS: Record<TaskPriority, string> = {
  high: styles.prioHigh,
  med: styles.prioMed,
  low: styles.prioLow,
};

const LINK_ICON: Record<TaskLink['type'], React.ReactNode> = {
  task: <CheckSquareOutlined />,
  artifact: <AppstoreOutlined />,
  note: <FileTextOutlined />,
};

const initials = (name: string): string => {
  if (name === 'You') {
    return 'You';
  }
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0])
    .join('')
    .toUpperCase();
};

export const TaskCheck: React.FC<{
  done: boolean;
  onToggle: () => void;
}> = ({ done, onToggle }) => (
  <button
    type="button"
    className={styles.check}
    aria-label={done ? 'Mark not done' : 'Mark done'}
    onClick={(e) => {
      e.stopPropagation();
      onToggle();
    }}
  >
    {/* Use AntD Checkbox-like affordance via icon */}
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        borderRadius: 4,
        border: '1.5px solid var(--app-color-border-secondary)',
        background: done ? 'var(--app-color-primary)' : 'transparent',
        color: '#fff',
        cursor: 'pointer',
      }}
    >
      {done ? <CheckOutlined style={{ fontSize: 11 }} /> : null}
    </span>
  </button>
);

export const DueChip: React.FC<{ due: string | null }> = ({ due }) => {
  const bucket = dueBucket(due);
  if (bucket === 'none') {
    return null;
  }
  const cls =
    bucket === 'overdue'
      ? styles.dueOverdue
      : bucket === 'today'
      ? styles.dueToday
      : '';
  return (
    <span className={`${styles.dueChip} ${cls}`}>
      <CalendarOutlined />
      {bucket === 'today' ? 'Today' : formatDue(due)}
    </span>
  );
};

// Count of authored stream entries (comments + notes), excluding lifecycle
// events — reflects the unified comments+notes stream shown in the detail.
export const streamCount = (task: Task): number =>
  (task.stream ?? []).filter((e) => e.kind === 'comment' || e.kind === 'note')
    .length;

export const StreamCountChip: React.FC<{ task: Task }> = ({ task }) => {
  const n = streamCount(task);
  if (!n) {
    return null;
  }
  return (
    <span
      className={styles.dueChip}
      title={`${n} comment${n === 1 ? '' : 's'} & note${n === 1 ? '' : 's'}`}
    >
      <MessageOutlined />
      {n}
    </span>
  );
};

export const AssistChip: React.FC = () => (
  <span
    className={`${styles.dueChip} ${styles.assistChip}`}
    title="Owl can help"
  >
    <ThunderboltOutlined />
    Owl can help
  </span>
);

export const BlockedChip: React.FC<{ blockers: Task[] }> = ({ blockers }) => {
  if (!blockers.length) {
    return null;
  }
  const label =
    blockers.length === 1
      ? `Blocked by ${blockers[0].title}`
      : `Blocked by ${blockers.length} tasks`;
  return (
    <Tooltip title={blockers.map((b) => b.title).join(', ')}>
      <span className={`${styles.dueChip} ${styles.blockedChip}`}>
        <ClockCircleOutlined />
        <span className={styles.blockedLabel}>{label}</span>
      </span>
    </Tooltip>
  );
};

export const LinkChip: React.FC<{
  link: TaskLink;
  onOpen?: (link: TaskLink) => void;
}> = ({ link, onOpen }) => (
  <button
    type="button"
    className={styles.linkChip}
    style={{
      cursor: onOpen ? 'pointer' : 'default',
      border: 'none',
      background: 'none',
      padding: 0,
    }}
    onClick={(e) => {
      e.stopPropagation();
      onOpen?.(link);
    }}
    title={link.label ? `Open ${link.label}` : undefined}
  >
    {LINK_ICON[link.type] ?? <LinkOutlined />}
    <span className={styles.linkChipLabel}>{link.label ?? link.type}</span>
  </button>
);

export const PriorityToggle: React.FC<{
  priority: TaskPriority;
  onChange: (p: TaskPriority) => void;
}> = ({ priority, onChange }) => (
  <button
    type="button"
    className={`${styles.prio} ${PRIORITY_CLASS[priority]}`}
    title={`Priority: ${PRIORITY_LABEL[priority]} (click to change)`}
    onClick={(e) => {
      e.stopPropagation();
      onChange(nextPriority(priority));
    }}
  >
    <span className={styles.prioDot} />
    <span>{PRIORITY_LABEL[priority]}</span>
  </button>
);

export const Assignee: React.FC<{ name: string | null }> = ({ name }) => {
  if (!name) {
    return null;
  }
  return (
    <span
      className={`${styles.assignee} ${
        name === 'You' ? styles.assigneeYou : ''
      }`}
      title={`Assigned to ${name}`}
    >
      {initials(name)}
    </span>
  );
};

export const SnoozeControl: React.FC<{
  onSnooze: (due: string) => void;
}> = ({ onSnooze }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <DatePicker
        open={open}
        onOpenChange={setOpen}
        value={null}
        onChange={(d) => {
          if (d) {
            onSnooze(dayjs(d).format('YYYY-MM-DD'));
          }
          setOpen(false);
        }}
        format="MMM D, YYYY"
        placeholder="Snooze…"
        className={styles.snooze}
        suffixIcon={<ClockCircleOutlined />}
        allowClear={false}
        inputReadOnly
        variant="borderless"
      />
    </span>
  );
};

// Tag chips shown on a task row/card. Clicking a chip toggles that tag in the
// panel filter; `active` highlights chips currently in the filter. Clicks are
// stopped so they filter without also opening the task detail.
export const TagChips: React.FC<{
  tags: string[];
  active?: string[];
  onTag?: (tag: string) => void;
}> = ({ tags, active, onTag }) => {
  if (!tags.length) {
    return null;
  }
  return (
    <span className={styles.tagChips}>
      {tags.map((tag) => (
        <button
          type="button"
          key={tag}
          className={clsx(
            styles.tagChip,
            active?.includes(tag.toLowerCase()) && styles.tagChipOn
          )}
          title={`Filter by #${tag}`}
          onClick={(e) => {
            e.stopPropagation();
            onTag?.(tag);
          }}
        >
          {tag}
        </button>
      ))}
    </span>
  );
};

// Filter bar listing every tag present on this dossier's tasks, each with a
// usage count. Toggling a chip adds/removes it from the active (union) filter.
export const TagFilterBar: React.FC<{
  tags: TagCount[];
  active: string[];
  onToggle: (tag: string) => void;
  onClear: () => void;
}> = ({ tags, active, onToggle, onClear }) => {
  if (!tags.length) {
    return null;
  }
  return (
    <div className={styles.filterBar}>
      <TagsOutlined className={styles.filterIcon} />
      <div className={styles.filterTags}>
        {tags.map(({ tag, count }) => (
          <button
            type="button"
            key={tag}
            className={clsx(
              styles.tagChip,
              active.includes(tag) && styles.tagChipOn
            )}
            onClick={() => onToggle(tag)}
          >
            {tag}
            <span className={styles.tagCount}>{count}</span>
          </button>
        ))}
      </div>
      {active.length ? (
        <button type="button" className={styles.filterClear} onClick={onClear}>
          Clear
          <CloseOutlined />
        </button>
      ) : null}
    </div>
  );
};

// Provenance chip shown when a task was raised by the Owl agent
// (task.raised_by === 'Owl').
export const OwlChip: React.FC = () => (
  <span className={styles.owlChip} title="Raised by Owl">
    <RobotOutlined />
    <span>Owl</span>
  </span>
);

export const isDone = (task: Task): boolean => task.status === 'done';
